import { useState, useCallback, useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api-2026.fikacreatives.co";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

const TIME_GROUPS = [
  { label: "Morning",   hours: [9, 10, 11],         short: "9–12" },
  { label: "Afternoon", hours: [12, 13, 14, 15, 16], short: "12–5" },
  { label: "Evening",   hours: [17, 18, 19],         short: "5–7"  },
];

function slotKey(day, hour) {
  return `${day} ${String(hour).padStart(2, "0")}:00`;
}

function daySlots(day) {
  return HOURS.map((h) => slotKey(day, h));
}

function groupSlots(day, group) {
  return group.hours.map((h) => slotKey(day, h));
}

export default function TypicalAvailabilityPicker({
  onSubmit,
  heading,
  buttonLabel = "Continue →",
  initialSlots,
  hideButton = false,
  onSlotsChange,
  alwaysShow = false,
}) {
  const [saved, setSaved] = useState(
    () => !alwaysShow && Array.isArray(initialSlots) && initialSlots.length > 0
  );
  const [selected, setSelected] = useState(() => {
    if (Array.isArray(initialSlots) && initialSlots.length > 0) {
      return new Set(initialSlots);
    }
    return new Set();
  });
  const [saving, setSaving] = useState(false);

  // Touch drag state
  const dragState = useRef({ active: false, adding: true });

  useEffect(() => {
    onSlotsChange?.(selected);
  }, [selected, onSlotsChange]);

  // ── cell toggle ──────────────────────────────────────────────────────────
  const toggleCell = useCallback((key, forceAdd) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const adding = forceAdd ?? !prev.has(key);
      adding ? next.add(key) : next.delete(key);
      return next;
    });
  }, []);

  // ── toggle whole day ─────────────────────────────────────────────────────
  function toggleDay(day) {
    const slots = daySlots(day);
    const allSelected = slots.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      allSelected
        ? slots.forEach((k) => next.delete(k))
        : slots.forEach((k) => next.add(k));
      return next;
    });
  }

  // ── toggle whole group row (e.g. all Mon–Sun mornings) ───────────────────
  function toggleGroupRow(group) {
    const slots = DAYS.flatMap((d) => groupSlots(d, group));
    const allSelected = slots.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      allSelected
        ? slots.forEach((k) => next.delete(k))
        : slots.forEach((k) => next.add(k));
      return next;
    });
  }

  // ── quick selects ────────────────────────────────────────────────────────
  function selectWeekdays() {
    const slots = ["Mon", "Tue", "Wed", "Thu", "Fri"].flatMap(daySlots);
    setSelected((prev) => {
      const next = new Set(prev);
      slots.forEach((k) => next.add(k));
      return next;
    });
  }

  function selectWeekends() {
    const slots = ["Sat", "Sun"].flatMap(daySlots);
    setSelected((prev) => {
      const next = new Set(prev);
      slots.forEach((k) => next.add(k));
      return next;
    });
  }

  function clearAll() {
    setSelected(new Set());
  }

  // ── mouse drag ───────────────────────────────────────────────────────────
  function handleCellMouseDown(key) {
    const adding = !selected.has(key);
    dragState.current = { active: true, adding };
    toggleCell(key, adding);
  }

  function handleCellMouseEnter(key) {
    if (!dragState.current.active) return;
    toggleCell(key, dragState.current.adding);
  }

  function handleMouseUp() {
    dragState.current.active = false;
  }

  // ── touch drag ───────────────────────────────────────────────────────────
  function handleCellTouchStart(e, key) {
    e.preventDefault();
    const adding = !selected.has(key);
    dragState.current = { active: true, adding };
    toggleCell(key, adding);
  }

  function handleTouchMove(e) {
    if (!dragState.current.active) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const key = el?.dataset?.slotKey;
    if (key) toggleCell(key, dragState.current.adding);
  }

  function handleTouchEnd() {
    dragState.current.active = false;
  }

  if (saved) return null;

  const isDayFull = (day) => daySlots(day).every((k) => selected.has(k));
  const isGroupRowFull = (group) =>
    DAYS.flatMap((d) => groupSlots(d, group)).every((k) => selected.has(k));

  return (
    <div
      style={s.wrapper}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {heading && (
        <div style={s.header}>
          <div style={s.headerTitle}>{heading}</div>
        </div>
      )}

      <p style={s.hint}>Tap cells or drag across to select. Tap a day to toggle the whole day.</p>

      {/* Quick selects */}
      <div style={s.quickRow}>
        <button style={s.quickBtn} onClick={selectWeekdays}>Weekdays</button>
        <button style={s.quickBtn} onClick={selectWeekends}>Weekends</button>
        <button style={{ ...s.quickBtn, color: "#9E3A3A" }} onClick={clearAll}>Clear</button>
      </div>

      {/* Grid */}
      <div style={s.gridScroll} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <table style={s.table} cellSpacing={0} cellPadding={0}>
          <thead>
            <tr>
              <th style={s.cornerTh} />
              {DAYS.map((day) => (
                <th
                  key={day}
                  style={{ ...s.dayTh, ...(isDayFull(day) ? s.dayThActive : {}) }}
                  onClick={() => toggleDay(day)}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIME_GROUPS.map((group, gi) => (
              <>
                {/* Group header row */}
                <tr key={`group-${gi}`}>
                  <td
                    style={{ ...s.groupLabelCell, ...(isGroupRowFull(group) ? s.groupLabelActive : {}) }}
                    onClick={() => toggleGroupRow(group)}
                  >
                    <span style={s.groupLabel}>{group.label}</span>
                    <span style={s.groupSub}>{group.short}</span>
                  </td>
                  {DAYS.map((day) => {
                    const slots = groupSlots(day, group);
                    const allActive = slots.every((k) => selected.has(k));
                    const someActive = !allActive && slots.some((k) => selected.has(k));
                    return (
                      <td
                        key={day}
                        style={{
                          ...s.groupCell,
                          ...(allActive ? s.groupCellFull : someActive ? s.groupCellPartial : s.groupCellEmpty),
                        }}
                        onClick={() => {
                          const adding = !allActive;
                          setSelected((prev) => {
                            const next = new Set(prev);
                            adding
                              ? slots.forEach((k) => next.add(k))
                              : slots.forEach((k) => next.delete(k));
                            return next;
                          });
                        }}
                      />
                    );
                  })}
                </tr>

                {/* Hour rows within group */}
                {group.hours.map((h, hi) => (
                  <tr key={`${gi}-${h}`}>
                    <td style={s.timeTd}>
                      {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
                    </td>
                    {DAYS.map((day) => {
                      const key = slotKey(day, h);
                      const active = selected.has(key);
                      const isLastInGroup = hi === group.hours.length - 1;
                      return (
                        <td
                          key={key}
                          data-slot-key={key}
                          style={{
                            ...s.cell,
                            ...(active ? s.cellActive : s.cellInactive),
                            ...(isLastInGroup ? s.cellGroupEnd : {}),
                          }}
                          onMouseDown={() => handleCellMouseDown(key)}
                          onMouseEnter={() => handleCellMouseEnter(key)}
                          onTouchStart={(e) => handleCellTouchStart(e, key)}
                        />
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={s.footer}>
        <div style={s.selectedCount}>
          {selected.size === 0
            ? "No slots selected"
            : `${selected.size} slot${selected.size !== 1 ? "s" : ""} selected`}
        </div>
        {!hideButton && (
          <button
            style={{ ...s.submitBtn, opacity: selected.size === 0 || saving ? 0.4 : 1 }}
            disabled={selected.size === 0 || saving}
            onClick={async () => {
              setSaving(true);
              try {
                await fetch(`${API_BASE}/api/settings/typical-availability`, {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ slots: Array.from(selected) }),
                });
              } catch (e) {
                console.error("typical-availability save failed", e);
              } finally {
                setSaving(false);
              }
              setSaved(true);
              onSubmit?.();
            }}
          >
            {saving ? "Saving…" : buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

const ACCENT = "#C8A882";
const ACCENT_LIGHT = "#F5EDE2";
const BORDER = "#E8E0D6";
const TEXT_MAIN = "#1a1a2e";
const TEXT_MUTED = "#9B8E83";

const s = {
  wrapper: {
    userSelect: "none",
    WebkitUserSelect: "none",
    marginTop: 12,
    width: "100%",
    boxSizing: "border-box",
  },
  header: {
    marginBottom: 8,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 15,
    fontFamily: "Suisse-Medium",
    color: TEXT_MAIN,
  },
  hint: {
    fontSize: 12,
    color: TEXT_MUTED,
    marginBottom: 10,
    marginTop: 0,
  },
  quickRow: {
    display: "flex",
    gap: 8,
    marginBottom: 12,
  },
  quickBtn: {
    background: "#F5F0EB",
    border: "1px solid " + BORDER,
    borderRadius: 20,
    padding: "5px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: TEXT_MAIN,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  gridScroll: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    width: "100%",
    borderRadius: 12,
    border: "1px solid " + BORDER,
    background: "#fff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  cornerTh: {
    width: 72,
    padding: 0,
  },
  dayTh: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: TEXT_MUTED,
    padding: "10px 4px",
    textAlign: "center",
    cursor: "pointer",
    borderBottom: "1px solid " + BORDER,
    transition: "background 0.1s, color 0.1s",
    userSelect: "none",
  },
  dayThActive: {
    color: ACCENT,
    background: ACCENT_LIGHT,
  },
  // Group header row
  groupLabelCell: {
    padding: "6px 8px",
    background: "#FAF7F4",
    borderTop: "2px solid " + BORDER,
    borderBottom: "1px solid " + BORDER,
    cursor: "pointer",
    verticalAlign: "middle",
  },
  groupLabelActive: {
    background: ACCENT_LIGHT,
  },
  groupLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: TEXT_MAIN,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  groupSub: {
    display: "block",
    fontSize: 10,
    color: TEXT_MUTED,
    marginTop: 1,
  },
  groupCell: {
    height: 28,
    cursor: "pointer",
    borderTop: "2px solid " + BORDER,
    borderLeft: "1px solid " + BORDER,
    transition: "background 0.1s",
  },
  groupCellEmpty: {
    background: "#FAF7F4",
  },
  groupCellPartial: {
    background: "#F0E6D8",
  },
  groupCellFull: {
    background: ACCENT,
  },
  // Hour cells
  timeTd: {
    fontSize: 10,
    color: TEXT_MUTED,
    textAlign: "right",
    paddingRight: 8,
    width: 72,
    height: 36,
    whiteSpace: "nowrap",
    borderBottom: "1px solid " + BORDER,
    boxSizing: "border-box",
  },
  cell: {
    height: 36,
    cursor: "pointer",
    borderLeft: "1px solid " + BORDER,
    borderBottom: "1px solid " + BORDER,
    transition: "background 0.07s",
    boxSizing: "border-box",
  },
  cellInactive: {
    background: "#fff",
  },
  cellActive: {
    background: ACCENT,
  },
  cellGroupEnd: {
    borderBottom: "none",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
    gap: 10,
    flexWrap: "wrap",
  },
  selectedCount: {
    fontSize: 13,
    color: TEXT_MUTED,
  },
  submitBtn: {
    background: TEXT_MAIN,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 22px",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 600,
    transition: "opacity 0.15s",
  },
};
